import React, { useState, useEffect } from 'react';
import { Plus, Download, Send, Eye, Edit, Filter, FileText, Search } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { StatusBadge } from '../ui/StatusBadge';
import { formatCurrency, formatDate } from '../../lib/utils';
import { quoteService } from '../../lib/database/quotes';
import { CreateQuoteModal } from './CreateQuoteModal';
import { EditQuoteModal } from './EditQuoteModal';
import { ViewQuoteModal } from './ViewQuoteModal';
import { ConvertToInvoiceModal } from './ConvertToInvoiceModal';

export const QuotesList: React.FC = () => {
  const [quotes, setQuotes] = useState<any[]>([]);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [selectedQuote, setSelectedQuote] = useState<any>(null);

  const fetchQuotes = async () => {
    try {
      setLoading(true);
      const { data } = await quoteService.getQuotes();
      if (data) {
        setQuotes(data);
      }
    } catch (error) {
      console.error('Error fetching quotes:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchQuotes();
  }, []);

  const handleCreateSuccess = () => {
    fetchQuotes(); // Refresh the list
  };

  const handleEditSuccess = () => {
    fetchQuotes(); // Refresh the list
    setSelectedQuote(null);
  };

  const handleViewQuote = (quote: any) => {
    setSelectedQuote(quote);
    setShowViewModal(true);
  };

  const handleEditQuote = (quote: any) => {
    setSelectedQuote(quote);
    setShowEditModal(true);
  };

  const handleEditFromView = () => {
    setShowViewModal(false);
    setShowEditModal(true);
  };

  const handleSendQuote = async (quoteId: string) => {
    try {
      await quoteService.updateQuote(quoteId, { status: 'sent' });
      fetchQuotes(); // Refresh to show updated status
    } catch (error) {
      console.error('Error sending quote:', error);
      alert('Failed to send quote. Please try again.');
    }
  };

  const handleApproveQuote = async (quoteId: string) => {
    if (window.confirm('Mark this quote as approved?')) {
      try {
        await quoteService.updateQuote(quoteId, { status: 'approved' });
        fetchQuotes(); // Refresh to show updated status
      } catch (error) {
        console.error('Error approving quote:', error);
        alert('Failed to approve quote. Please try again.');
      }
    }
  };

  const handleConvertToInvoice = (quote: any) => {
    setSelectedQuote(quote);
    setShowConvertModal(true);
  };

  const handleConvertSuccess = () => {
    fetchQuotes(); // Refresh to show updated status
    setSelectedQuote(null);
  };

  const filteredQuotes = quotes.filter(quote => {
    const matchesSearch = quote.quote_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         quote.client?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         quote.client?.company?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         quote.client?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         quote.notes?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         quote.total?.toString().includes(searchTerm);
    
    const matchesFilter = filter === 'all' || quote.status === filter;
    
    return matchesSearch && matchesFilter;
  });

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="bg-white p-6 rounded-xl h-96"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Quotes</h1>
          <p className="text-gray-600 mt-2">Manage and track all your quotes</p>
        </div>
        <Button 
          icon={Plus} 
          className="shadow-lg"
          onClick={() => setShowCreateModal(true)}
        >
          Create Quote
        </Button>
      </div>

      {/* Search and Filters */}
      <Card padding="sm">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search quotes, clients, amounts..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 w-80 text-sm"
            />
          </div>
          
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <Filter className="w-4 h-4 text-gray-500" />
              <span className="text-sm font-medium text-gray-700">Filter:</span>
            </div>
            <div className="flex space-x-2">
              {['all', 'draft', 'sent', 'approved', 'rejected'].map((status) => (
                <button
                  key={status}
                  onClick={() => setFilter(status)}
                  className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors duration-200 ${
                    filter === status
                      ? 'bg-primary-100 text-primary-700'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                </button>
              ))}
            </div>
          </div>
        </div>
      </Card>

      {/* Enhanced Quotes Table */}
      <Card padding="none" className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
              <tr>
                <th className="text-left py-4 px-6 font-semibold text-gray-900 text-sm uppercase tracking-wider">Quote</th>
                <th className="text-left py-4 px-6 font-semibold text-gray-900 text-sm uppercase tracking-wider">Client</th>
                <th className="text-left py-4 px-6 font-semibold text-gray-900 text-sm uppercase tracking-wider">Amount</th>
                <th className="text-left py-4 px-6 font-semibold text-gray-900 text-sm uppercase tracking-wider">Status</th>
                <th className="text-left py-4 px-6 font-semibold text-gray-900 text-sm uppercase tracking-wider">Valid Until</th>
                <th className="text-right py-4 px-6 font-semibold text-gray-900 text-sm uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredQuotes.length === 0 ? (
                <tr>
                  <td colSpan={6} className="py-12 text-center text-gray-500 bg-gray-50">
                    {searchTerm || filter !== 'all' ? 'No quotes found matching your criteria.' : 'No quotes found. Create your first quote!'}
                  </td>
                </tr>
              ) : (
                filteredQuotes.map((quote, index) => (
                  <tr
                    key={quote.id}
                    className={`transition-all duration-200 hover:bg-purple-50 hover:shadow-sm ${
                      index % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'
                    }`}
                  >
                    <td className="py-4 px-6 border-l-4 border-transparent hover:border-secondary-400">
                      <div>
                        <p className="font-semibold text-gray-900">{quote.quote_number}</p>
                        <p className="text-sm text-gray-500">
                          Created {formatDate(quote.created_at)}
                        </p>
                      </div>
                    </td>
                    <td className="py-4 px-6">
                      <div>
                        <p className="font-medium text-gray-900">{quote.client?.name || 'Unknown'}</p>
                        <p className="text-sm text-gray-500">{quote.client?.company || 'No company'}</p>
                      </div>
                    </td>
                    <td className="py-4 px-6">
                      <p className="font-bold text-lg text-gray-900">
                        {formatCurrency(Number(quote.total))}
                      </p>
                    </td>
                    <td className="py-4 px-6">
                      <StatusBadge status={quote.status} />
                    </td>
                    <td className="py-4 px-6">
                      <p className="text-gray-900 font-medium">{formatDate(quote.valid_until)}</p>
                      {new Date(quote.valid_until) < new Date() && quote.status !== 'approved' && (
                        <p className="text-sm text-error-600 font-medium">Expired</p>
                      )}
                    </td>
                    <td className="py-4 px-6 text-right">
                      <div className="flex items-center justify-end space-x-2">
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          icon={Eye}
                          onClick={() => handleViewQuote(quote)}
                          className="hover:bg-blue-100"
                        >
                          View
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          icon={Edit}
                          onClick={() => handleEditQuote(quote)}
                          className="hover:bg-yellow-100"
                        >
                          Edit
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          icon={Download}
                          className="hover:bg-green-100"
                        >
                          PDF
                        </Button>
                        {quote.status === 'draft' && (
                          <Button 
                            variant="primary" 
                            size="sm" 
                            icon={Send}
                            onClick={() => handleSendQuote(quote.id)}
                          >
                            Send
                          </Button>
                        )}
                        {quote.status === 'sent' && (
                          <Button 
                            variant="success" 
                            size="sm"
                            onClick={() => handleApproveQuote(quote.id)}
                          >
                            Approve
                          </Button>
                        )}
                        {quote.status === 'approved' && (
                          <Button 
                            variant="secondary" 
                            size="sm"
                            icon={FileText}
                            onClick={() => handleConvertToInvoice(quote)}
                          >
                            Convert to Invoice
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card padding="sm" className="text-center bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
          <p className="text-2xl font-bold text-purple-700">{filteredQuotes.length}</p>
          <p className="text-sm text-purple-600">{searchTerm || filter !== 'all' ? 'Filtered' : 'Total'} Quotes</p>
        </Card>
        <Card padding="sm" className="text-center bg-gradient-to-br from-success-50 to-success-100 border-success-200">
          <p className="text-2xl font-bold text-success-700">
            {filteredQuotes.filter(q => q.status === 'approved').length}
          </p>
          <p className="text-sm text-success-600">Approved</p>
        </Card>
        <Card padding="sm" className="text-center bg-gradient-to-br from-warning-50 to-warning-100 border-warning-200">
          <p className="text-2xl font-bold text-warning-700">
            {filteredQuotes.filter(q => q.status === 'sent').length}
          </p>
          <p className="text-sm text-warning-600">Pending</p>
        </Card>
        <Card padding="sm" className="text-center bg-gradient-to-br from-error-50 to-error-100 border-error-200">
          <p className="text-2xl font-bold text-error-700">
            {filteredQuotes.filter(q => q.status === 'rejected').length}
          </p>
          <p className="text-sm text-error-600">Rejected</p>
        </Card>
      </div>

      {/* Modals */}
      <CreateQuoteModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={handleCreateSuccess}
      />

      <EditQuoteModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedQuote(null);
        }}
        onSuccess={handleEditSuccess}
        quote={selectedQuote}
      />

      <ViewQuoteModal
        isOpen={showViewModal}
        onClose={() => {
          setShowViewModal(false);
          setSelectedQuote(null);
        }}
        onEdit={handleEditFromView}
        onConvertToInvoice={() => handleConvertToInvoice(selectedQuote)}
        quote={selectedQuote}
      />

      <ConvertToInvoiceModal
        isOpen={showConvertModal}
        onClose={() => {
          setShowConvertModal(false);
          setSelectedQuote(null);
        }}
        onSuccess={handleConvertSuccess}
        quote={selectedQuote}
      />
    </div>
  );
};